import type { Assignment, Activity, DamagedObject } from "../api/assignments/Assignments.domain";

export type PrefillSource = "activity" | "additionalCharacteristics" | "damagedObject";

export interface ActivityPrefillConfig {
  fieldSources?: Partial<Record<string, PrefillSource[]>>;
}

type PrefillEntry = {
  /** Key used in activity dynamicProperties and additionalCharacteristics. */
  lookupKey: string;
  /** When set, value is also read from damagedManufacturedObject[objectProp]. */
  objectProp?: keyof DamagedObject;
};

const PREFILL: Record<string, PrefillEntry> = {
  IMEI: { lookupKey: "IMEI" },
  FEATURES: { lookupKey: "STORAGE_CAPACITY" },
  EUROPEAN_ARTICLE_NUMBERING_CODE: {
    lookupKey: "EUROPEAN_ARTICLE_NUMBERING_CODE",
    objectProp: "europeanArticleNumberingCode",
  },
  SERIALNUMBER: { lookupKey: "SERIALNUMBER", objectProp: "serialNumber" },
  MARKETBRANDNAME: { lookupKey: "MARKETBRANDNAME", objectProp: "marketBrandName" },
  MODELNAME: { lookupKey: "MODELNAME", objectProp: "modelName" },
};

const DEFAULT_CONFIG: ActivityPrefillConfig = {};

function getFromActivities(activities: Activity[] | undefined, fieldCode: string): string {
  if (!activities?.length) return "";
  for (const act of activities) {
    const value = act.responseDetail?.dynamicProperties?.find(
      (p) => String(p.key) === String(fieldCode),
    )?.value;
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
}

function getFromAdditionalCharacteristics(assignment: Assignment, fieldCode: string): string {
  const list = assignment.damagedManufacturedObject?.additionalCharacteristics;
  if (!list?.length) return "";
  const key = String(fieldCode);
  for (const char of list) {
    const spec = char.basedOnAdditionalSpecification;
    if (spec && String(spec.key) === key && spec.value != null && String(spec.value).trim() !== "")
      return String(spec.value).trim();
  }
  return "";
}

function getFromDamagedObject(assignment: Assignment, prop: keyof DamagedObject): string {
  const obj = assignment.damagedManufacturedObject;
  if (!obj) return "";
  const value = (obj as unknown as Record<string, unknown>)[prop];
  if (value == null || String(value).trim() === "") return "";
  return String(value).trim();
}

/**
 * Returns the first non-empty value for the given field code.
 * Only fields listed in PREFILL are prefilled; order: damagedObject (if objectProp) → activity → additionalCharacteristics.
 */
export function getActivityPrefillValue(
  assignment: Assignment,
  fieldCode: string,
  _config: ActivityPrefillConfig = DEFAULT_CONFIG,
): string {
  const entry = PREFILL[String(fieldCode)];
  if (!entry) return "";

  const { lookupKey, objectProp } = entry;

  if (objectProp) {
    const v = getFromDamagedObject(assignment, objectProp);
    if (v) return v;
  }
  const v1 = getFromActivities(assignment.activities, lookupKey);
  if (v1) return v1;
  const v2 = getFromAdditionalCharacteristics(assignment, lookupKey);
  if (v2) return v2;
  return "";
}

export { DEFAULT_CONFIG as defaultActivityPrefillConfig };
